import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { trackReportFeature } from "@/analytics/report-analytics";
import APIClient from "@/APIClient";
import { QueryKey } from "./query-keys";
import { useToast } from "@/composable/useToast";
import { useTranslation } from "i18next-vue";
import { type Ref } from "vue";
import type {
  Diagnostic,
  PaginationParams,
  UpdateDiagnosticPayload,
} from "@/types";
import { FileType } from "@/types/file.types";

export const useGetDiagnostics = (
  pagination: Ref<PaginationParams>,
  projectId: Ref<number | undefined>,
) => {
  const isSomeDataExtracting = (diagnostics: Diagnostic[]) => {
    return diagnostics.some(({ isExtractingData }) => isExtractingData);
  };

  const queryClient = useQueryClient();

  const queryKey = [QueryKey.DIAGNOSTICS, { projectId }];

  return useQuery({
    queryKey,
    queryFn: async () => {
      const res = await APIClient.fetchAllDiagnostics({
        projectId: projectId.value,
        ...pagination.value,
      });
      if (isSomeDataExtracting(res)) {
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey });
        }, 5000);
      }
      return res;
    },

    refetchOnWindowFocus: false,
  });
};

export const useGetDiagnostic = (id: Ref<number | undefined>) => {
  const queryClient = useQueryClient();
  const queryKey = [QueryKey.DIAGNOSTICS, id];

  return useQuery({
    queryKey,
    queryFn: async () => {
      if (!id.value) throw new Error("No diagnostic id provided");
      const res = await APIClient.fetchDiagnostic(id.value);
      if (res.isExtractingData) {
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey });
        }, 5000);
      }
      return res;
    },
    enabled: () => !!id.value,
  });
};

export const useStartDiagnosticDataExtraction = (
  diagnosticId: Ref<number | undefined>,
  reportTemplateId?: Ref<number | undefined>,
) => {
  const queryClient = useQueryClient();
  const { showWarning } = useToast();

  return useMutation({
    mutationFn: async () => {
      if (!diagnosticId.value) throw new Error("No diagnostic id provided");
      return await APIClient.startDiagnosticDataExtraction(
        diagnosticId.value,
        reportTemplateId?.value,
      );
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DIAGNOSTICS, diagnosticId.value],
      });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === QueryKey.REPORTS,
      });
    },
    onError: () => {
      showWarning("toast:diagnostics.extraction-error");
    },
  });
};

export const useSetDefaultReport = (diagnosticId: Ref<number | undefined>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reportId: number | null) => {
      if (!diagnosticId.value) throw new Error("No diagnostic id provided");
      return await APIClient.setDefaultReport(diagnosticId.value, reportId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DIAGNOSTICS, diagnosticId.value],
      });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === QueryKey.REPORTS,
      });
    },
  });
};

export const useDeleteManyDiagnostics = () => {
  const queryClient = useQueryClient();
  const { showWarning } = useToast();

  return useMutation({
    mutationFn: async (ids: number[]) => {
      return await APIClient.deleteManyDiagnostics(ids);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.DIAGNOSTICS] });
    },
    onError: () => {
      showWarning("toast:diagnostics.delete-many-error");
    },
  });
};

export const useDeleteDiagnostic = (id: Ref<number | undefined>) => {
  const queryClient = useQueryClient();
  const { showWarning } = useToast();
  return useMutation({
    mutationFn: async () => {
      if (!id.value) throw new Error("No diagnostic id provided");
      return await APIClient.deleteManyDiagnostics([id.value]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.DIAGNOSTICS] });
    },
    onError: () => {
      showWarning("toast:diagnostics.delete-error");
    },
  });
};

export const useUpdateDiagnostic = (id: Ref<number | undefined>) => {
  const { showSuccess, showLoading } = useToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateDiagnosticPayload) => {
      if (!id.value) throw new Error("No diagnostic id provided");
      const handle = showLoading("toast:diagnostics.saving");
      return await APIClient.updateDiagnostic(id.value, payload).finally(() => {
        handle.close();
      });
    },
    onSuccess: () => {
      showSuccess("toast:diagnostics.save-success");
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DIAGNOSTICS, id],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DIAGNOSTICS],
      });
    },
  });
};

export const useGenerateReport = (
  reportId: Ref<number | undefined>,
  reportName: Ref<string | undefined>,
) => {
  const queryClient = useQueryClient();
  const { showSuccess, showWarning, showLoading } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async () => {
      if (!reportId.value) throw new Error("No report id provided");
      const notif = showLoading("toast:reports.generating");

      return await APIClient.generateReportDocx(reportId.value)
        .then((blob) => {
          const src = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = src;
          const fileName = (
            reportName.value || t("report:diagnostics.untitledReport")
          ).replace(/[\\/:*?"<>|]/g, "_");
          link.download = `${fileName}.docx`;
          // Certains navigateurs (Edge, Firefox) ignorent un click() sur un
          // élément détaché : il faut l'ajouter au DOM avant de cliquer.
          document.body.appendChild(link);
          link.click();
          link.remove();
          // Libère le blob une fois le téléchargement déclenché.
          setTimeout(() => URL.revokeObjectURL(src), 1000);
        })
        .finally(() => {
          notif.close();
        });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORTS, reportId.value],
      });
      showSuccess("toast:reports.generate-success");
    },
    onError: () => {
      showWarning("toast:reports.generate-error");
    },
  });
};

export const useCreateDiagnostic = (projectId: Ref<number | undefined>) => {
  const queryClient = useQueryClient();
  const { showSuccess, showWarning } = useToast();

  return useMutation({
    mutationFn: async (name?: string) => {
      if (!projectId.value) throw new Error("No project id provided");
      return await APIClient.createDiagnostic(projectId.value, name);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.DIAGNOSTICS] });
      showSuccess("toast:diagnostics.create-success");
    },
    onError: () => {
      showWarning("toast:diagnostics.create-error");
    },
  });
};

export const useApplyPartsSuggestions = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showWarning } = useToast();

  return useMutation({
    mutationFn: async (diagnosticId: number) => {
      return await APIClient.applyPartsSuggestions(diagnosticId);
    },
    onSuccess: (_, diagnosticId) => {
      showSuccess("toast:diagnostics.apply-parts-success");
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FIELD_PARTS, { diagnosticId }],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DIAGNOSTICS, diagnosticId],
      });
    },
    onError: (error: Error & { response?: { message?: string } }) => {
      const message =
        error.response?.message || "toast:diagnostics.apply-parts-error";
      showWarning(message);
    },
  });
};

// DEPRECATED: Use useGetDiagnosticImages from diagnostic-images.query.ts instead
// This function returns FileEntity[] while the new one returns DiagnosticImage[]
export const useGetDiagnosticImageFiles = (
  diagnosticId: Ref<number | undefined>,
) => {
  return useQuery({
    queryKey: [QueryKey.FILE, FileType.DIAGNOSTIC_IMAGE, diagnosticId],
    queryFn: async () => {
      if (!diagnosticId.value) throw new Error("No diagnostic id provided");
      return await APIClient.fetchDiagnosticImages(diagnosticId.value);
    },
    enabled: () => !!diagnosticId.value,
    refetchOnWindowFocus: false,
  });
};

export const useGetDiagnosticSectionsTree = (
  diagnosticId: Ref<number | undefined>,
) => {
  return useQuery({
    queryKey: [QueryKey.DIAGNOSTICS, "tree", diagnosticId],
    queryFn: async () => {
      if (!diagnosticId.value) throw new Error("No diagnostic id provided");
      return await APIClient.fetchDiagnosticSectionsTree(diagnosticId.value);
    },
    enabled: () => !!diagnosticId.value,
    refetchOnWindowFocus: false,
  });
};

export const useReorderAppendices = (diagnosticId: Ref<number>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return await APIClient.reorderDiagnosticAppendices(diagnosticId.value);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DIAGNOSTICS, "appendices", diagnosticId.value],
      });
    },
  });
};

// ==========================================
// Findings Generation (AI)
// ==========================================

export const useStartFindingsGeneration = (
  diagnosticId: Ref<number | undefined>,
) => {
  const queryClient = useQueryClient();
  const { showWarning } = useToast();

  return useMutation({
    mutationFn: async () => {
      if (!diagnosticId.value) throw new Error("No diagnostic id provided");
      return await APIClient.startFindingsGeneration(diagnosticId.value);
    },
    onSuccess: () => {
      trackReportFeature("findings_generated_ai");
      // Invalidate diagnostic to get updated generation status
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DIAGNOSTICS, diagnosticId],
      });
    },
    onError: () => {
      showWarning("toast:diagnostics.findings-generation-error");
    },
  });
};

export const useGetFindingsGenerationStatus = (
  diagnosticId: Ref<number | undefined>,
  enabled: Ref<boolean>,
) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [QueryKey.DIAGNOSTICS, "findings-generation", diagnosticId],
    queryFn: async () => {
      if (!diagnosticId.value) throw new Error("No diagnostic id provided");
      return await APIClient.getFindingsGenerationStatus(diagnosticId.value);
    },
    enabled: () => !!diagnosticId.value && enabled.value,
    refetchInterval: (query) => {
      // Poll every 2 seconds while generating
      if (query.state.data?.isGenerating) {
        return 2000;
      }
      // Stop polling when done
      if (query.state.data && !query.state.data.isGenerating) {
        // Invalidate related queries when generation is complete
        queryClient.invalidateQueries({
          queryKey: [QueryKey.DIAGNOSTICS, "tree", diagnosticId.value],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKey.DIAGNOSTIC_IMAGES],
        });
      }
      return false;
    },
    refetchOnWindowFocus: false,
  });
};

// ==========================================
// Modular Generation (AI Hub)
// ==========================================

export const useStartModularGeneration = (
  diagnosticId: Ref<number | undefined>,
  reportId: Ref<number | undefined>,
) => {
  const queryClient = useQueryClient();
  const { showWarning } = useToast();

  return useMutation({
    mutationFn: async (modules: string[]) => {
      if (!diagnosticId.value) throw new Error("No diagnostic id provided");
      if (!reportId.value) throw new Error("No report id provided");
      return await APIClient.startModularGeneration(
        diagnosticId.value,
        reportId.value,
        modules,
      );
    },
    onSuccess: (_data, modules) => {
      trackReportFeature("modular_generation_started", {
        modules: modules.join(","),
      });
      // Invalidate diagnostic to get updated generation status
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DIAGNOSTICS, diagnosticId],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DIAGNOSTICS, "modular-generation", reportId.value],
      });
      // Invalidate all findings queries (sections + unsorted) to clear old data
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === QueryKey.FINDINGS,
      });
      // Invalidate sections queries to load newly created sections
      queryClient.invalidateQueries({
        queryKey: [QueryKey.SECTIONS],
      });
    },
    onError: () => {
      showWarning("toast:diagnostics.modular-generation-error");
    },
  });
};

export const useGetModularGenerationStatus = (
  diagnosticId: Ref<number | undefined>,
  reportId: Ref<number | undefined>,
  enabled: Ref<boolean>,
) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [QueryKey.DIAGNOSTICS, "modular-generation", reportId],
    queryFn: async () => {
      if (!diagnosticId.value) throw new Error("No diagnostic id provided");
      if (!reportId.value) throw new Error("No report id provided");
      return await APIClient.getModularGenerationStatus(
        diagnosticId.value,
        reportId.value,
      );
    },
    enabled: () => !!diagnosticId.value && !!reportId.value && enabled.value,
    refetchInterval: (query) => {
      // Poll every 2 seconds while generating
      if (query.state.data?.isGenerating) {
        return 2000;
      }
      // Stop polling when done
      if (query.state.data && !query.state.data.isGenerating) {
        // Invalidate related queries when generation is complete
        queryClient.invalidateQueries({
          queryKey: [QueryKey.SECTIONS],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKey.DIAGNOSTIC_IMAGES],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKey.FIELD_OBSERVATIONS, diagnosticId.value],
        });
        // Invalidate all findings queries (sections + unsorted) to load new data
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === QueryKey.FINDINGS,
        });
      }
      return false;
    },
    refetchOnWindowFocus: false,
  });
};
